import { Injectable, EventEmitter } from '@angular/core';
import { async } from '@angular/core/testing';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { CookieService } from 'ngx-cookie-service';
import { CoreConfigService } from '../config/core-config.service';
import { UserContextService } from './user-context.service';
import { AuthServerProvider } from '../auth/auth-jwt.service';
import { ReadyState, ReadyStateFlag } from './ready-state.model';


@Injectable({ providedIn:'root' })
export class AppSettingService {
    private _commonSettingsMap: {[key:string]:string}|Promise<any> = null;
    private _communityGroupSettingsMap: {[key:string]:{[key:string]:string}|Promise<any>} = {};
    private _communitySettingsMap: {[key:string]:{[key:string]:string}|Promise<any>} = {};
    private _times = 0;

    private _photoBaseUrl: string;

    commonSettingReadyStateChange: EventEmitter<ReadyState> = new EventEmitter();
    officeSettingReadyStateChange: EventEmitter<ReadyState> = new EventEmitter();


    constructor(
        private http: HttpClient,
        private authServerProvider: AuthServerProvider,
        private configService: CoreConfigService,
        private userContext: UserContextService,
        private cookieService: CookieService,
    ) {
        this.load();
        this.userContext.currentOfficeChange.subscribe(office=>{
            if(office) this.getSettings(office);
        });
    }


    private get url() : string {
        return this.configService.apiUrls.webappSettings;
    }

    private load(communityGroupId?:string, communityId?:string): Promise<{[key:string]:string}> {

        const params = { domainName: document.domain };
        let promise;
        if(communityId){
            promise = this._communitySettingsMap[communityId];
            params['communityId'] = communityId;
        }else if(communityGroupId){
            promise = this._communityGroupSettingsMap[communityId];
            params['communityGroupId'] = communityGroupId;
        }else{
            promise = this._commonSettingsMap;
        }

        if(promise != null){
            return (promise instanceof Promise) ? promise : Promise.resolve(promise);
        }

        const readyStateChange = communityGroupId==null&&communityId==null
            ?this.commonSettingReadyStateChange:this.officeSettingReadyStateChange;

        const doHttpGet = ()=>{
            promise = this.http.get<{[key:string]:string}>(this.url, { headers: { 'NoAuthorization':'true'}, params: params }).toPromise()
            .then(data=>{
                if(communityId) this._communitySettingsMap[communityId] = data;
                else if(communityGroupId) this._communityGroupSettingsMap[communityGroupId] = data;
                else this._commonSettingsMap = data;
    
                try{
                    const domainConfigLoginPage = JSON.parse(data[this.configService.settingKeys.DOMAIN_CONFIG_LOGIN_PAGE]);
                    this.configService.mergeConfig({loginPage: domainConfigLoginPage});
                }catch{}
                try{
                    const domainConfigMainPage = JSON.parse(data[this.configService.settingKeys.DOMAIN_CONFIG_MAIN_PAGE]);
                    this.configService.mergeConfig({mainPage: domainConfigMainPage});
                }catch{}

                readyStateChange.emit({state:ReadyStateFlag.READY, message:'系统加载成功'});
                this._times = 0;
                return data;
            })
            .catch(error=>{
                console.log("AppSettingService.load() --> error:" + error.message);
                this._times++;
                if(this._times < 10){
                    if(error.state == 401 || (this._times > 2 && communityId==null && communityGroupId==null)){
                        this.authServerProvider.clearAuthenticationToken();
                    }
                    setTimeout(()=>{doHttpGet();}, 2000);
                }else{
                   console.log("AppSettingService.load() --> error: retry times over 10, do not retry!");
                   this._times = 0;
                    readyStateChange.emit({state:ReadyStateFlag.ERROR, message:'加载系统初始化参数失败，请稍后重试或与管理员联系！'});
                }
                return {};
            });

            if(communityId) this._communitySettingsMap[communityId] = promise;
            else if(communityGroupId) this._communityGroupSettingsMap[communityGroupId] = promise;
            else this._commonSettingsMap = promise;
        }

        doHttpGet();
        readyStateChange.emit({state:ReadyStateFlag.LOADING, message:'加载系统中 ...'});
        return promise;
    }

    getSettings(office?:any): Promise<{[key:string]:string}> {
        return this.tryGetOfficeSettings(office, true);
    }

    getCommonSettings() {
        if(this._commonSettingsMap instanceof Promise) return null;
        return this._commonSettingsMap;
    }
    getOfficeSettings(office?:any){
        return this.tryGetOfficeSettings(office, false);
    }
    
    private tryGetOfficeSettings(office?:any, needPromise?:boolean){
        let communityGroupId;
        let communityId;
        let settingsMap;
        if(office){
            if(office.officeType=="COMMUNITY"){
                communityId = office.id;
                settingsMap = this._communitySettingsMap[office.id];
            }else if(office.officeType=="COMMUNITY_GROUP"){
                communityGroupId = office.id;
                settingsMap = this._communityGroupSettingsMap[communityGroupId];
            }else if(office.ownerCommunityGroup){
                communityGroupId = office.ownerCommunityGroup.id;
                settingsMap = this._communityGroupSettingsMap[communityGroupId];
            }else{
                settingsMap = this._commonSettingsMap;
            }
        }else{
            settingsMap = this._commonSettingsMap;
        }
        // console.log("tryGetOfficeSettings() -> ", communityGroupId, communityId);

        if(settingsMap == null)
            settingsMap = this.load(communityGroupId,communityId);

        return settingsMap instanceof Promise 
            ? (needPromise? settingsMap:null)
            : (needPromise? Promise.resolve(settingsMap):settingsMap);
    }

    /**
     * 获取公共配置参数值
     */
    getCommonSettingValue(key:string): string{
        if(this._commonSettingsMap instanceof Promise) return "";
        return this._commonSettingsMap?this._commonSettingsMap[key]:"";
    }

    get photoBaseUrl() : string {
        if(!this._photoBaseUrl){
            let url = this.getCommonSettingValue(this.configService.settingKeys.PHOTO_BASE_URL);
            this._photoBaseUrl = url.lastIndexOf('/')==url.length-1 ? url : url+'/';
        }
        return this._photoBaseUrl;
    }
    get copyrightInfo() : string {
        return this.getCommonSettingValue(this.configService.settingKeys.COPYRIGHT);
    }
    get headerLogoIcon() : string {
        return this.getCommonSettingValue(this.configService.settingKeys.LOGO_HEADER_ICON);
    }
    get headerLogoFull() : string {
        return this.getCommonSettingValue(this.configService.settingKeys.LOGO_HEADER_FULL);
    }
    get loginPageLogo() : string {
        return this.getCommonSettingValue(this.configService.settingKeys.LOGO_LOGIN);
    }
    get weiXinAuthHtml() : string {
        return this.getCommonSettingValue(this.configService.settingKeys.WEIXIN_AUTH_HTML);
    }
    get baseClientAppPackageDownloadUrl() : string {
        return this.getCommonSettingValue(this.configService.settingKeys.BASE_CLIENT_APP_PACKAGE_DOWNLOAD_URL);
    }
}
